import React, { useEffect, useState } from "react";
import FoodMenuService from "../../../Services/QRCodeServices/CustomerService/FoodMenuService";
import { Col, Row } from "reactstrap";
import { Triangle } from "react-loader-spinner";
import { MenuItem } from "./MenuItem";
import { OrderMenu } from "./OrderMenu";
import { Pagination } from "./Pagination";
import TableService from "../../../Services/QRCodeServices/TableSerivce/TableService";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper";

export const FoodMenu = () => {
  const [menuData, setMenuData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;
  const [userInfo, setUserInfo] = useState(null);
  const [orgCode, setOrgCode] = useState(null);
  const [selectedTable, setSelectedTable] = useState(null);

  useEffect(() => {
    const storedUserInfo = localStorage.getItem("user");
    if (storedUserInfo) {
      const parsedUserInfo = JSON.parse(storedUserInfo);
      setUserInfo(parsedUserInfo);
      setOrgCode(parsedUserInfo?.organizationInfo?.orgCode || null);
    }
  }, []);

  useEffect(() => {
    const fetchMenuData = async () => {
      if (!orgCode) {
        setError("Organization code is required");
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        const data = await FoodMenuService.getFoodMenu(orgCode);

        if (!data || !data.allMenu) {
          throw new Error("Invalid menu data received");
        }

        // Filter out categories with no menu items
        const filteredMenuData = data.allMenu.filter(
          (category) => category.menuItem && category.menuItem.length > 0
        );

        setMenuData(filteredMenuData);

        // If the currently selected category is now empty, switch to 'all'
        if (
          selectedCategory !== "all" &&
          !filteredMenuData.find((cat) => cat.name === selectedCategory)
        ) {
          setSelectedCategory("all");
        }
      } catch (error) {
        setError(
          error.response?.status === 500
            ? "Server error. Please try again later."
            : "Failed to load menu. Please check your connection and try again."
        );
        console.error("Failed to fetch menu data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMenuData();
  }, [orgCode]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCategory]);

  const handleOrderUpdate = async () => {
    // Refresh the table data in OrderMenu component
    if (selectedTable) {
      try {
        const response = await TableService.getActiveTableList();
        const updatedTableData = response.find(
          (table) => table.id === selectedTable.id
        );
        // Update the selected table data
        setSelectedTable((prevState) => ({
          ...prevState,
          ...updatedTableData,
        }));
      } catch (error) {
        console.error("Error refreshing table data:", error);
        toast.error("Failed to refresh table data");
      }
    }
  };
  const getAllMenuItems = () => {
    return menuData.reduce((items, category) => {
      return [...items, ...category.menuItem];
    }, []);
  };

  const getFilteredContent = () => {
    let filteredItems = [];

    if (selectedCategory === "all") {
      filteredItems = getAllMenuItems();
    } else {
      const selectedCategoryData = menuData.find(
        (category) => category.name === selectedCategory
      );
      filteredItems = selectedCategoryData ? selectedCategoryData.menuItem : [];
    }

    return filteredItems.filter((item) =>
      item.title?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const getPaginatedContent = () => {
    const filteredContent = getFilteredContent();
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredContent.slice(startIndex, startIndex + itemsPerPage);
  };

  const totalPages = Math.ceil(getFilteredContent().length / itemsPerPage);

  const handleCategoryClick = (categoryName) => {
    setSelectedCategory(categoryName);
  };

  // Check if there are any menu items at all
  const hasMenuItems = menuData.some(
    (category) => category.menuItem && category.menuItem.length > 0
  );

  if (isLoading) {
    return (
      <div className="text-center h-100 d-flex w-100 justify-content-center align-items-center p-4">
        <Triangle
          visible={true}
          height="80"
          width="80"
          color="#5B71B9"
          ariaLabel="triangle-loading"
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
      </div>
    );
  }

  if (!hasMenuItems) {
    return (
      <div className="alert alert-info" role="alert">
        No menu items available at the moment.
      </div>
    );
  }

  return (
    <Row>
      <Col md={8} className="container">
        <Row className="mb-4 container">
          <Col md={6} xs={12}>
            <h4 className="m-0 p-0">{userInfo.organizationInfo.name}</h4>
          </Col>
          <Col md={6} xs={12}>
            <input
              type="search"
              className="form-control"
              placeholder="Search items..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Col>
        </Row>

        {/* Only show categories section if there are categories with items */}
        {menuData.length > 0 && (
          <div className="mb-4 container position-relative">
            <Swiper
              modules={[Navigation]}
              spaceBetween={15}
              slidesPerView={"auto"}
              navigation
              className="pb-2"
            >
              {/* All Categories */}
              <SwiperSlide style={{ width: "auto" }}>
                <div
                  className={`cursor-pointer ${
                    selectedCategory === "all"
                      ? "border-bottom border-primary"
                      : ""
                  }`}
                  onClick={() => handleCategoryClick("all")}
                >
                  <div className="text-center p-2 bg-white rounded-3 shadow-sm">
                    <p className="fw-bold mb-0 text-black">All Categories</p>
                  </div>
                </div>
              </SwiperSlide>

              {/* Dynamic Categories */}
              {menuData.map((category, index) => (
                <SwiperSlide key={index} style={{ width: "auto" }}>
                  <div
                    className={`cursor-pointer ${
                      selectedCategory === category.name
                        ? "border-bottom border-primary"
                        : ""
                    }`}
                    onClick={() => handleCategoryClick(category.name)}
                  >
                    <div className="text-center p-2 bg-white rounded-3 shadow-sm">
                      <p className="fw-bold mb-0 text-black">{category.name}</p>
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        )}

        <Row className="p-0 m-0">
          {getPaginatedContent().map((item, index) => (
            <MenuItem
              key={item.id || index}
              item={item}
              selectedTable={selectedTable}
              onOrderUpdate={handleOrderUpdate}
            />
          ))}
        </Row>

        {totalPages > 1 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        )}
      </Col>
      <Col md={4}>
        <OrderMenu
          selectedTable={selectedTable}
          setSelectedTable={setSelectedTable}
        />
      </Col>
    </Row>
  );
};
